import React, { useEffect, useState, useRef, useCallback } from 'react'
import Layout from '../../components/Layout/Layout'
import axios from 'axios'
import moment from 'moment'
import '../../styles/CashierStyle.css'
import { useAuth } from '../../context/auth'

const SaleRecords = () => {
  const [auth] = useAuth()
  const [records, setRecords] = useState([])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const today = moment().format('YYYY-MM-DD')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [appliedFrom, setAppliedFrom] = useState(today)
  const [appliedTo, setAppliedTo] = useState(today)

  const [totals, setTotals] = useState({
    totalPayment: 0,
    totalQty: 0,
    totalOrders: 0,
  })
  const PER_PAGE = 30
  const observer = useRef()
  const didInitialFetch = useRef(false)

  const getSaleRecords = async (
    currentPage,
    reset = false,
    from = appliedFrom,
    to = appliedTo
  ) => {
    try {
      const userId = auth?.user?._id
      if (!userId) return

      setLoading(true)
      const { data } = await axios.get('/api/v1/order/sale-records', {
        params: {
          page: currentPage,
          userId,
          fromDate: from,
          toDate: to,
        },
      })

      const incoming = data?.records || []

      if (reset && data?.totals) {
        setTotals(data.totals)
      }

      // ✅ KEY FIX: if returned < PER_PAGE, no more pages
      if (incoming.length < PER_PAGE) {
        setHasMore(false)
      } else {
        setHasMore(true)
      }

      if (incoming.length > 0) {
        setRecords((prev) => (reset ? incoming : [...prev, ...incoming]))
      } else {
        // no results for this page
        if (reset) setRecords([])
        setHasMore(false)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const userId = auth?.user?._id
    if (!userId) return
    if (didInitialFetch.current) return
    didInitialFetch.current = true

    setHasMore(true)
    setRecords([])
    setPage(1)

    getSaleRecords(1, true, appliedFrom, appliedTo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?._id])

  useEffect(() => {
    const userId = auth?.user?._id
    if (!userId) return
    if (page === 1) return

    getSaleRecords(page, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, auth?.user?._id])

  const lastElementRef = useCallback(
    (node) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })

      if (node) observer.current.observe(node)
    },
    [loading, hasMore]
  )

  const handleSubmitDateRange = async () => {
    if (!fromDate || !toDate) return
    if (fromDate > toDate) return

    setAppliedFrom(fromDate)
    setAppliedTo(toDate)

    if (observer.current) observer.current.disconnect()
    setHasMore(true)
    setRecords([])
    setPage(1)
    await getSaleRecords(1, true, fromDate, toDate)
  }

  const handleResetDateRange = async () => {
    const todayStr = moment().format('YYYY-MM-DD')

    setFromDate(todayStr)
    setToDate(todayStr)
    setAppliedFrom(todayStr)
    setAppliedTo(todayStr)

    if (observer.current) observer.current.disconnect()
    setHasMore(true)
    setRecords([])
    setPage(1)

    await getSaleRecords(1, true, todayStr, todayStr)
  }

  return (
    <Layout title={'Transaction Records'}>
      <div className='col-md-12'>
        <div className='col-md-12'>
          <div
            className='searchInventory shadow-sm p-3 bg-white rounded'
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              height: '60px',
              position: 'fixed',
              zIndex: 1000,
              gap: '10px',
            }}
          >
            <div style={{ flex: 1 }}>
              <h5 style={{ margin: 0, color: '#49759e' }}>
                Transaction Records
              </h5>
            </div>

            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                Total Qty: {totals.totalQty || 0}
                <span className='mx-3'>|</span>
                Total Payment: RM {Number(totals.totalPayment || 0).toFixed(2)}
              </div>
            </div>

            <div
              style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}
            >
              <div
                style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
              >
                <div className='d-flex align-items-center'>
                  <span className='me-2' style={{ fontSize: 14 }}>
                    From
                  </span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={fromDate}
                    max={toDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{ width: 140 }}
                  />
                </div>

                <div className='d-flex align-items-center'>
                  <span className='me-2' style={{ fontSize: 14 }}>
                    To
                  </span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={toDate}
                    min={fromDate}
                    max={today}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{ width: 140 }}
                  />
                </div>
                <button
                  type='button'
                  className='btn btn-primary btn-sm'
                  onClick={handleSubmitDateRange}
                  disabled={loading || !fromDate || !toDate}
                >
                  Submit
                </button>

                <button
                  type='button'
                  className='btn btn-warning btn-sm'
                  onClick={handleResetDateRange}
                  disabled={loading}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className='table-responsive'>
            <table className='table actable' style={{ marginTop: '70px' }}>
              <thead>
                <tr>
                  <th scope='col'>Date</th>
                  <th scope='col'>Order ID</th>
                  <th scope='col' className='text-center'>
                    Item Qty
                  </th>
                  <th scope='col' className='text-end'>
                    Total (RM)
                  </th>
                  <th scope='col'>Payment</th>
                  <th scope='col'>Reference No.</th>
                </tr>
              </thead>

              <tbody>
                {records.map((record, recordIndex) => (
                  <React.Fragment key={record._id}>
                    <tr className='sale-records'>
                      <td>
                        {moment(record.orderdate)
                          .subtract(8, 'hours')
                          .format('YYYY-MM-DD HH:mm')}
                      </td>
                      <td>{record.orderid}</td>
                      <td className='text-center'>
                        {record.products.reduce(
                          (sum, p) => sum + (p.quantity || 0),
                          0
                        )}
                      </td>
                      <td className='text-end'>
                        {(Math.round(record.totalorders * 100) / 100).toFixed(
                          2
                        )}
                      </td>
                      <td>{record.paymentmethod}</td>
                      <td>{record.paymentrefno}</td>
                    </tr>
                    <tr key={`product-details-${record._id}`}>
                      <td colSpan={6}>
                        {record.products.map((product, productIndex) => (
                          <div
                            key={`product-${record._id}-${productIndex}`}
                            className='mb-1'
                            style={{ fontSize: '17px' }}
                          >
                            {productIndex + 1}.{' '}
                            <span className='ms-1 me-2'>{product.refcode}</span>{' '}
                            {product.name}{' '}
                            <span className='ms-2'>x {product.quantity}</span>
                            <span className='ms-3'>
                              RM{' '}
                              {(
                                Math.round(
                                  product.quantity * product.price * 100
                                ) / 100
                              ).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </td>
                    </tr>
                    {recordIndex === records.length - 1 && hasMore && (
                      <tr
                        ref={lastElementRef}
                        key={`last-row-${record._id}`}
                        style={{ height: 0, padding: 0, border: 0 }}
                      >
                        <td
                          colSpan={6}
                          style={{
                            height: 0,
                            padding: 0,
                            border: 0,
                            lineHeight: 0,
                          }}
                        />
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '12px 0',
                }}
              >
                Loading...
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default SaleRecords
